import { useEffect, useMemo, useRef } from 'react'

import { serialiseOccupancy } from 'lib/search/searchUtils'
import useQueryParams from 'hooks/useQueryParams'
import { useAppSelector } from 'hooks/reduxHooks'
import { reportLog } from 'services/userMonitoring'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'

interface Params {
  enabled: boolean
  found: boolean
  price: number | undefined
  rateId: string | undefined
  expectedRateId: string | undefined
  propertyId: string | undefined
  checkIn: string | undefined
  checkOut: string | undefined
  rooms: Array<OfferPageRoom>
  missingReason?: string
}

export default function useLogMetasearchInterestedOption(params: Params) {
  const {
    enabled,
    found,
    price,
    rateId,
    expectedRateId,
    propertyId,
    checkIn,
    checkOut,
    rooms,
    missingReason,
  } = params
  const queryParams = useQueryParams()
  const userAgent = useAppSelector(state => state.system.rawUserAgentString)
  const region = useAppSelector(state => state.geo.currentRegionCode)
  const occupancy = useMemo(() => serialiseOccupancy(rooms), [rooms])
  const queryParamsObject = useMemo(() => Object.fromEntries(queryParams.entries()), [queryParams])
  const isMetasearchTraffic = queryParams.get('source') === 'metasearch' || queryParams.get('utm_content') === 'gha-lpp' || queryParams.get('gha_price') === '1'
  const hasSelectedDates = !!checkIn && !!checkOut
  const logKey = useMemo(() => JSON.stringify({
    userAgent,
    region,
    found,
    price,
    rateId,
    expectedRateId,
    propertyId,
    checkIn,
    checkOut,
    occupancy,
    queryParams: queryParamsObject,
    missingReason,
  }), [checkIn, checkOut, expectedRateId, found, missingReason, occupancy, price, propertyId, queryParamsObject, rateId, region, userAgent])
  const loggedKeyRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!enabled || !isMetasearchTraffic || !hasSelectedDates || loggedKeyRef.current === logKey) {
      return
    }

    if (!found) {
      loggedKeyRef.current = logKey
      reportLog('Metasearch interested option missing', {
        userAgent,
        region,
        expectedRateId,
        rateId,
        propertyId,
        checkIn,
        checkOut,
        occupancy,
        missingReason,
        queryParams: queryParamsObject,
      }, 'warn')
      return
    }

    if (price === undefined) {
      return
    }

    loggedKeyRef.current = logKey
    reportLog('Metasearch interested option shown', {
      userAgent,
      region,
      price,
      rateId,
      expectedRateId,
      propertyId,
      checkIn,
      checkOut,
      occupancy,
      queryParams: queryParamsObject,
    }, 'info')
  }, [checkIn, checkOut, enabled, expectedRateId, found, hasSelectedDates, isMetasearchTraffic, logKey, missingReason, occupancy, price, propertyId, queryParamsObject, rateId, region, userAgent])
}
